import _ from 'lodash';

/**
 * Extracts Instagram handle from various input formats
 * @param {string} input - Instagram handle, URL, or @ mention
 * @returns {string|null} - Clean Instagram handle or null if invalid
 */
export const extractInstagramHandle = input => {
  if (!input || !_.isString(input)) {
    return null;
  }

  const cleaned = _.trim(input);

  // If it's already just a handle (no URL), return it cleaned
  if (!_.includes(cleaned, '/') && !_.includes(cleaned, '.')) {
    return _.replace(cleaned, /^@/, '');
  }

  try {
    // Add protocol if missing
    let urlString = cleaned;
    if (!urlString.match(/^https?:\/\//)) {
      urlString = `https://${urlString}`;
    }

    const url = new URL(urlString);

    // Check if it's an Instagram domain
    if (!_.includes(url.hostname, 'instagram.com')) {
      // If it's not Instagram, maybe it's just a handle with dots/slashes
      return _.chain(cleaned)
        .replace(/^@/, '')
        .replace(/[\/\.]/g, '')
        .value();
    }

    // Extract handle from Instagram URL path
    const pathParts = _.filter(_.split(url.pathname, '/'), part => _.size(part) > 0);

    if (_.size(pathParts) > 0) {
      return _.head(pathParts);
    }

    return null;
  } catch (error) {
    // If URL parsing fails, treat as plain handle
    return _.chain(cleaned)
      .replace(/^@/, '')
      .replace(/[\/\.]/g, '')
      .value();
  }
};

/**
 * Validates Instagram handle against official Instagram rules
 * @param {string} handle - Instagram handle to validate
 * @returns {object} - Validation result with isValid, error, handle, warnings
 */
export const validateInstagramHandle = handle => {
  if (!handle || !_.isString(handle)) {
    return {
      isValid: false,
      error: 'Handle is required',
      handle: null,
    };
  }

  const cleaned = _.toLower(_.trim(handle));

  const validation = {
    isValid: true,
    error: null,
    handle: cleaned,
    warnings: [],
  };

  // Length check (1-30 characters)
  if (_.size(cleaned) < 1) {
    validation.isValid = false;
    validation.error = 'Username cannot be empty';
    return validation;
  }

  if (_.size(cleaned) > 30) {
    validation.isValid = false;
    validation.error = 'Username cannot be longer than 30 characters';
    return validation;
  }

  // Character validation - only letters, numbers, periods, and underscores
  const validCharRegex = /^[a-z0-9._]+$/;
  if (!validCharRegex.test(cleaned)) {
    validation.isValid = false;
    validation.error = 'Username can only contain letters, numbers, periods, and underscores';
    return validation;
  }

  // Cannot start or end with period
  if (_.startsWith(cleaned, '.') || _.endsWith(cleaned, '.')) {
    validation.isValid = false;
    validation.error = 'Username cannot start or end with a period';
    return validation;
  }

  // Warnings for potentially problematic handles
  if (_.includes(cleaned, '__')) {
    validation.warnings.push('Multiple consecutive underscores may look unprofessional');
  }

  if (_.size(_.split(cleaned, '.')) > 3) {
    validation.warnings.push('Many periods in username may be confusing');
  }

  return validation;
};

/**
 * Processes single-column CSV data and extracts/validates Instagram handles
 * @param {Array} csvData - Array of CSV row objects (single column)
 * @returns {object} - Processed results with validation summary
 */
export const processInfluencerCSV = csvData => {
  const results = _.map(csvData, (row, index) => {
    const result = { originalRow: index + 1 };

    // Get the first (and only) column value
    const firstKey = _.head(_.keys(row));
    const instagramInput = firstKey ? _.get(row, firstKey) : null;

    if (instagramInput && _.trim(String(instagramInput))) {
      const cleanInput = _.trim(String(instagramInput));
      const extractedHandle = extractInstagramHandle(cleanInput);

      if (extractedHandle) {
        const validation = validateInstagramHandle(extractedHandle);

        result.instagramHandle = validation.isValid ? validation.handle : null;
        result.originalInstagramInput = cleanInput;
        result.extractedHandle = extractedHandle;
        result.isValid = validation.isValid;
        result.validationError = validation.error;
        result.validationWarnings = validation.warnings || [];
      } else {
        result.isValid = false;
        result.validationError = `Could not extract handle from: "${cleanInput}"`;
        result.originalInstagramInput = cleanInput;
      }
    } else {
      result.isValid = false;
      result.validationError = 'Empty or missing Instagram data';
    }

    return result;
  });

  // Check for duplicates
  const validResults = _.filter(results, 'isValid');
  const handleCounts = _.countBy(validResults, 'instagramHandle');
  const duplicates = _.keys(_.pickBy(handleCounts, count => count > 1));

  _.forEach(results, result => {
    if (result.isValid && _.includes(duplicates, result.instagramHandle)) {
      result.validationWarnings.push('Duplicate handle in this CSV');
    }
  });

  const summary = {
    total: _.size(csvData),
    valid: _.size(_.filter(results, 'isValid')),
    invalid: _.size(_.filter(results, r => !r.isValid)),
    withWarnings: _.size(_.filter(results, r => _.get(r, 'validationWarnings.length', 0) > 0)),
    duplicates: _.size(duplicates),
    overLimit: false,
  };

  // Check if we exceed the 150 influencer limit (after removing duplicates)
  const uniqueValidInfluencers = _.uniqBy(_.filter(results, 'isValid'), 'instagramHandle');
  if (_.size(uniqueValidInfluencers) > 150) {
    summary.overLimit = true;
    summary.validAfterDedup = _.size(uniqueValidInfluencers);
  }

  return { results, summary, duplicates, uniqueValidInfluencers };
};

/**
 * Test cases for Instagram handle extraction and validation
 */
export const testInstagramHandleExtraction = () => {
  const testCases = [
    'kimkardashian',
    '@kimkardashian',
    'https://instagram.com/kimkardashian?igsh=N3FzdmJrOHBzejhh',
    'https://instagram.com/kimkardashian/',
    'https://www.instagram.com/kimkardashian?igsh=N3FzdmJrOHBzejhh',
    'instagram.com/kimkardashian',
    'www.instagram.com/kimkardashian',
    'https://instagram.com/kimkardashian/p/somepost/',
    'invalid input',
    '',
    null,
  ];

  console.log('Instagram Handle Extraction Tests:');
  _.forEach(testCases, test => {
    const extracted = extractInstagramHandle(test);
    const validation = extracted ? validateInstagramHandle(extracted) : { isValid: false };
    console.log(`"${test}" -> "${extracted}" (valid: ${validation.isValid})`);
  });
};



/** WEBPACK FOOTER **
 ** ./src/components/csv-upload-modal/instagram-utils.js
 **/